import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionType } from 'vopay';
import useFetchACHWithdrawTransaction from '@/hooks/useApi/useFetchACHWithdrawTransaction';
import AchWithdrawalTransactionInfo from './ACHWithdrawalTransactionInfo';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type AchWithdrawalTransactionProps = {
  TransactionID: string;
} & Omit<ComponentProps<typeof AchWithdrawalTransactionInfo>, 'transaction' | 'parentTransactionId' | 'parentType'>;

export default function AchWithdrawalTransactionContainer({
  TransactionID,
  Notes,
  isParent = false,
  jwt,
  ELinxRequestID = '',
  ...props
}: AchWithdrawalTransactionProps) {
  const { userSettings } = useUserSettingsProvider();
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { achWithdrawTransaction, fetchAchWithdrawTransaction, resetAchWithdrawTransaction } =
    useFetchACHWithdrawTransaction({ bearer: jwt });

  useEffect(() => {
    blockingLoader.show();
    resetAchWithdrawTransaction();

    fetchAchWithdrawTransaction({ TransactionID, Timezone: userSettings.timezone })
      .catch((error) =>
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred')),
      )
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  const transaction = achWithdrawTransaction;

  const fundingParentTransaction = Object.values(transaction?.ParentTransaction || {}).find(
    (parent) => parent.TransactionType === TransactionType.ACH_FUNDING,
  );

  return (
    <div>
      {transaction && (
        <AchWithdrawalTransactionInfo
          transaction={transaction}
          Notes={Notes}
          isParent={isParent}
          jwt={jwt}
          ELinxRequestID={ELinxRequestID}
          parentTransactionId={fundingParentTransaction?.TransactionID}
          parentType={fundingParentTransaction ? 'ACH Funding' : undefined}
          {...props}
        />
      )}
    </div>
  );
}
